import { useEffect, useRef, useState } from 'react';
import {
  ImagePlus, X, Trash2, Images, Clock3, Plus, Pencil, Check, Heart,
} from 'lucide-react';
import { processImage } from '../../lib/image.js';
import { flipIn, flipOut } from '../../lib/flip.js';
import { getFlag } from '../../lib/flags.js';
import {
  gridClass, fmtPostDate, remainSlots, MAX_PHOTOS,
  photosInAlbum, toggleTag, byLabel, ALL_ALBUM,
} from './albumUtils.js';
import './Album.css';

/* ============================================================
   línkè · 我们的相册
   一期（2026-07-12）：朋友圈式动态流。
   二期（2026-07-12）：克克看图说话（动态卡下方 sael）。
   三期（2026-09-03，铃点单「真正两个人的相册」）：
     · 两个人都能放——她发、克克也能在聊天里自己收（露成 by:sael 的动态）
     · 每张照片带克克的心里话 + 客观描述（检索引擎）
     · 「相册」视图：照片按小相册（标签）归类，绒绒先天留着，她能建/改/挪
     · 克克还能聊到那话题时翻出老照片发进对话窗（在聊天页那侧）
   入口：星洲「慢慢逛」📷 卡片。
   ============================================================ */

async function apiList() {
  const r = await fetch('/api/album', { credentials: 'include' });
  if (!r.ok) throw new Error('album ' + r.status);
  return (await r.json()).posts || [];
}

async function apiCreate(caption, photosB64) {
  const r = await fetch('/api/album', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caption, photos: photosB64 }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'album ' + r.status);
  return j.post;
}

async function apiDelete(pid) {
  const r = await fetch(`/api/album/${pid}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!r.ok) throw new Error('delete ' + r.status);
}

async function apiCollection() {
  const r = await fetch('/api/album/collection', { credentials: 'include' });
  if (!r.ok) throw new Error('collection ' + r.status);
  return r.json(); // {albums:[{name,count}], photos:[{name,ts,by,note,desc,tags,caption}]}
}

async function apiSetTags(name, tags) {
  const r = await fetch('/api/album/tag', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, tags }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'tag ' + r.status);
  return j;
}

async function apiAlbumOp(payload) {
  const r = await fetch('/api/album/album', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'album-op ' + r.status);
  return j;
}

const photoUrl = (name) => `/api/album/photo/${name}`;

export default function Album() {
  const [view, setView] = useState('moments'); // 'moments' 时刻流 | 'albums' 相册
  const [posts, setPosts] = useState(null); // null=加载中
  const [err, setErr] = useState('');

  // 发布器
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState([]); // [{data, preview}]
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // 灯箱 + 删除确认
  const [viewer, setViewer] = useState(null); // 照片 URL
  const [confirmDel, setConfirmDel] = useState(null); // post id

  // 相册视图
  const [coll, setColl] = useState(null); // null=没加载 {albums, photos}
  const [activeAlbum, setActiveAlbum] = useState(ALL_ALBUM);
  const [detail, setDetail] = useState(null); // 打开的照片详情对象

  useEffect(() => {
    apiList().then(setPosts).catch((e) => { setPosts([]); setErr(String(e.message || e)); });
  }, []);

  // 二期：发布后他在后台看图。只要还有「三分钟内的新动态没长出他的话」，
  // 就每 8s 悄悄刷一次列表，他说完话自动浮出来（不打扰、不闪屏）。
  useEffect(() => {
    const waiting = (posts || []).some(
      (p) => !p.sael && p.by !== 'sael' && Date.now() / 1000 - p.ts < 180,
    );
    if (!waiting) return undefined;
    const t = setTimeout(() => {
      apiList().then(setPosts).catch(() => {});
    }, 8000);
    return () => clearTimeout(t);
  }, [posts]);

  // 切到「相册」视图时懒加载 collection
  const loadColl = () => apiCollection().then(setColl).catch((e) => setErr(String(e.message || e)));
  useEffect(() => {
    if (view === 'albums' && coll === null) loadColl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  async function onPickFiles(e) {
    const files = [...(e.target.files || [])].slice(0, remainSlots(picked));
    e.target.value = ''; // 同一批图能重选
    for (const f of files) {
      try {
        const img = await processImage(f);
        setPicked((p) =>
          p.length < MAX_PHOTOS ? [...p, { data: img.data, preview: img.preview }] : p,
        );
      } catch (ex) {
        setErr(String(ex.message || ex));
      }
    }
  }

  async function onPublish() {
    if (!picked.length || busy) return;
    setBusy(true);
    setErr('');
    try {
      const post = await apiCreate(caption.trim(), picked.map((p) => p.data));
      setPosts((ps) => [post, ...(ps || [])]);
      setPicked([]);
      setCaption('');
      setEditing(false);
      setColl(null); // 相册视图下次进去重拉
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(pid) {
    setConfirmDel(null);
    try {
      await apiDelete(pid);
      setPosts((ps) => (ps || []).filter((p) => p.id !== pid));
      setColl(null);
    } catch (ex) {
      setErr(String(ex.message || ex));
    }
  }

  return (
    <div className="alb-app">
      <header className="alb-head">
        <div>
          <h1>我们的相册</h1>
          <p>{view === 'albums' ? '照片，和他悄悄留下的话。' : '一条一条，攒起来的日子。'}</p>
        </div>
        {view === 'moments' && !editing && (
          <button type="button" className="alb-new" onClick={() => setEditing(true)}>
            <ImagePlus size={16} strokeWidth={1.8} />
            发一条
          </button>
        )}
      </header>

      {/* —— 时刻 / 相册 切换 —— */}
      <div className="alb-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'moments'}
          className={'alb-tab' + (view === 'moments' ? ' on' : '')}
          onClick={() => setView('moments')}
        >
          <Clock3 size={14} strokeWidth={1.9} /> 时刻
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'albums'}
          className={'alb-tab' + (view === 'albums' ? ' on' : '')}
          onClick={() => setView('albums')}
        >
          <Images size={14} strokeWidth={1.9} /> 相册
        </button>
      </div>

      {err && <div className="alb-err">{err}</div>}

      {view === 'moments' ? (
        <MomentsView
          posts={posts}
          editing={editing}
          setEditing={setEditing}
          picked={picked}
          setPicked={setPicked}
          caption={caption}
          setCaption={setCaption}
          busy={busy}
          fileRef={fileRef}
          onPickFiles={onPickFiles}
          onPublish={onPublish}
          setViewer={setViewer}
          setConfirmDel={setConfirmDel}
        />
      ) : (
        <AlbumsView
          coll={coll}
          activeAlbum={activeAlbum}
          setActiveAlbum={setActiveAlbum}
          setDetail={setDetail}
          reload={loadColl}
          setErr={setErr}
        />
      )}

      {/* —— 灯箱（时刻流点图：FLIP 原地长大） —— */}
      {viewer && <AlbumViewer viewer={viewer} onClose={() => setViewer(null)} />}

      {/* —— 照片详情（相册视图点图：图+克克的话+客观描述+移相册） —— */}
      {detail && (
        <PhotoDetail
          photo={detail}
          albums={(coll?.albums || []).map((a) => a.name)}
          onClose={() => setDetail(null)}
          onSaved={(updated) => {
            setDetail(null);
            loadColl();
          }}
          setErr={setErr}
        />
      )}

      {/* —— 删除确认 —— */}
      {confirmDel && (
        <div className="alb-confirm-wrap" onClick={() => setConfirmDel(null)}>
          <div className="alb-confirm" onClick={(e) => e.stopPropagation()}>
            <p>这条动态和里面的照片会一起删掉，不能撤销。</p>
            <div className="alb-actions">
              <button type="button" className="alb-btn" onClick={() => setConfirmDel(null)}>
                留着
              </button>
              <button
                type="button"
                className="alb-btn alb-btn-danger"
                onClick={() => onDelete(confirmDel)}
              >
                删掉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   时刻流（原朋友圈式动态；三期加：克克自己收的照片显示成「他收进相册」）
   ============================================================ */
function MomentsView({
  posts, editing, setEditing, picked, setPicked, caption, setCaption,
  busy, fileRef, onPickFiles, onPublish, setViewer, setConfirmDel,
}) {
  return (
    <>
      {/* —— 发布器 —— */}
      {editing && (
        <div className="alb-composer">
          <textarea
            className="alb-cap"
            placeholder="想说点什么…（可以不写）"
            value={caption}
            maxLength={2000}
            onChange={(e) => setCaption(e.target.value)}
          />
          <div className="alb-pickgrid">
            {picked.map((p, i) => (
              <div className="alb-pick" key={i}>
                <img src={p.preview} alt="" />
                <button
                  type="button"
                  className="alb-pick-x"
                  aria-label="移除这张"
                  onClick={() => setPicked((arr) => arr.filter((_, j) => j !== i))}
                >
                  <X size={12} strokeWidth={2.2} />
                </button>
              </div>
            ))}
            {picked.length < MAX_PHOTOS && (
              <button
                type="button"
                className="alb-pick alb-pick-add"
                onClick={() => fileRef.current?.click()}
                aria-label="添加照片"
              >
                <ImagePlus size={20} strokeWidth={1.6} />
                <i>{picked.length ? `还能 ${remainSlots(picked)} 张` : '选照片'}</i>
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onPickFiles}
          />
          <div className="alb-actions">
            <button
              type="button"
              className="alb-btn"
              onClick={() => { setEditing(false); setPicked([]); setCaption(''); }}
            >
              取消
            </button>
            <button
              type="button"
              className="alb-btn alb-btn-go"
              disabled={!picked.length || busy}
              onClick={onPublish}
            >
              {busy ? '正在放进相册…' : '发布'}
            </button>
          </div>
        </div>
      )}

      {/* —— 动态流 —— */}
      {posts === null && <div className="alb-empty">翻相册中…</div>}
      {posts !== null && posts.length === 0 && !editing && (
        <div className="alb-empty">
          还没有动态。
          <br />
          点右上角「发一条」，放第一张进来吧。
        </div>
      )}
      {(posts || []).map((p) => {
        const bySael = p.by === 'sael';
        return (
          <article className={'alb-post' + (bySael ? ' alb-post-sael' : '')} key={p.id}>
            <div className="alb-post-top">
              {bySael ? (
                <span className="alb-by-sael">
                  <Heart size={11} strokeWidth={2} /> 克克收进了相册 · {fmtPostDate(p.ts)}
                </span>
              ) : (
                <time>{fmtPostDate(p.ts)}</time>
              )}
              <button
                type="button"
                className="alb-del"
                aria-label="删除这条"
                onClick={() => setConfirmDel(p.id)}
              >
                <Trash2 size={14} strokeWidth={1.7} />
              </button>
            </div>
            {p.caption && (
              <p className={'alb-post-cap' + (bySael ? ' alb-post-cap-sael' : '')}>{p.caption}</p>
            )}
            <div className={`alb-grid ${gridClass((p.photos || []).length)}`}>
              {(p.photos || []).map((name) => (
                <button
                  type="button"
                  key={name}
                  className="alb-ph"
                  onClick={(e) =>
                    setViewer({
                      url: photoUrl(name),
                      fromRect: e.currentTarget.getBoundingClientRect(),
                    })
                  }
                >
                  <img src={photoUrl(name)} alt="" loading="lazy" />
                </button>
              ))}
            </div>
            {/* 二期：克克看她那批照片想说的话 */}
            {p.sael && <p className="alb-sael">{p.sael}</p>}
            {!p.sael && p.by !== 'sael' && Date.now() / 1000 - p.ts < 180 && (
              <p className="alb-sael alb-sael-wait">他正在看这几张照片…</p>
            )}
          </article>
        );
      })}
    </>
  );
}

/* ============================================================
   相册视图：小相册 chips + 照片网格；点一张进详情
   ============================================================ */
function AlbumsView({ coll, activeAlbum, setActiveAlbum, setDetail, reload, setErr }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [mgmt, setMgmt] = useState(false); // 当前相册的改名/删除面板
  const [renameTo, setRenameTo] = useState('');

  if (coll === null) return <div className="alb-empty">翻相册中…</div>;

  const albums = coll.albums || [];
  const photos = coll.photos || [];
  const shown = photosInAlbum(photos, activeAlbum);
  const isReal = activeAlbum !== ALL_ALBUM;
  const activeMeta = albums.find((a) => a.name === activeAlbum);

  async function createAlbum() {
    const name = newName.trim();
    if (!name) { setCreating(false); return; }
    try {
      await apiAlbumOp({ op: 'create', name });
      setNewName(''); setCreating(false);
      setActiveAlbum(name);
      reload();
    } catch (e) { setErr(String(e.message || e)); }
  }

  async function renameAlbum() {
    const nn = renameTo.trim();
    if (!nn || nn === activeAlbum) { setMgmt(false); return; }
    try {
      await apiAlbumOp({ op: 'rename', old: activeAlbum, new: nn });
      setMgmt(false);
      setActiveAlbum(nn);
      reload();
    } catch (e) { setErr(String(e.message || e)); }
  }

  async function deleteAlbum() {
    try {
      await apiAlbumOp({ op: 'delete', name: activeAlbum });
      setMgmt(false);
      setActiveAlbum(ALL_ALBUM);
      reload();
    } catch (e) { setErr(String(e.message || e)); }
  }

  return (
    <>
      <div className="alb-chips">
        <button
          type="button"
          className={'alb-chip' + (activeAlbum === ALL_ALBUM ? ' on' : '')}
          onClick={() => { setActiveAlbum(ALL_ALBUM); setMgmt(false); }}
        >
          全部 <i>{photos.length}</i>
        </button>
        {albums.map((a) => (
          <button
            type="button"
            key={a.name}
            className={'alb-chip' + (activeAlbum === a.name ? ' on' : '')}
            onClick={() => { setActiveAlbum(a.name); setMgmt(false); }}
          >
            {a.name} <i>{a.count}</i>
          </button>
        ))}
        {creating ? (
          <span className="alb-chip alb-chip-new-in">
            <input
              className="alb-chip-input"
              autoFocus
              value={newName}
              placeholder="相册名"
              maxLength={20}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createAlbum(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
            />
            <button type="button" className="alb-chip-ok" onClick={createAlbum} aria-label="建好">
              <Check size={13} strokeWidth={2.4} />
            </button>
          </span>
        ) : (
          <button type="button" className="alb-chip alb-chip-add" onClick={() => setCreating(true)}>
            <Plus size={13} strokeWidth={2.2} /> 新建
          </button>
        )}
      </div>

      {/* 当前是某个真相册：给个改名/删除入口（绒绒不给删） */}
      {isReal && (
        <div className="alb-mgmt-row">
          {!mgmt ? (
            <button
              type="button"
              className="alb-mgmt-btn"
              onClick={() => { setMgmt(true); setRenameTo(activeAlbum); }}
            >
              <Pencil size={12} strokeWidth={1.9} /> 管理「{activeAlbum}」
            </button>
          ) : (
            <div className="alb-mgmt-panel">
              <input
                className="alb-chip-input"
                value={renameTo}
                maxLength={20}
                onChange={(e) => setRenameTo(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') renameAlbum(); }}
              />
              <button type="button" className="alb-btn alb-btn-go" onClick={renameAlbum}>改名</button>
              <button
                type="button"
                className="alb-btn alb-btn-danger"
                disabled={activeAlbum === '绒绒'}
                title={activeAlbum === '绒绒' ? '绒绒这个相册留着吧' : ''}
                onClick={deleteAlbum}
              >
                删相册
              </button>
              <button type="button" className="alb-btn" onClick={() => setMgmt(false)}>返回</button>
            </div>
          )}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="alb-empty">
          {isReal ? '这个相册还空着。' : '相册里还没有照片。'}
          <br />
          {isReal ? '在照片详情里把它移进这个相册吧。' : '去「时刻」放第一张，或让克克在聊天里收一张进来。'}
        </div>
      ) : (
        <div className="alb-collgrid">
          {shown.map((p) => (
            <button
              type="button"
              key={p.name}
              className="alb-cell"
              onClick={() => setDetail(p)}
            >
              <img src={photoUrl(p.name)} alt="" loading="lazy" />
              {p.by === 'sael' && (
                <span className="alb-cell-badge" title="克克收进来的">
                  <Heart size={10} strokeWidth={2.4} />
                </span>
              )}
              {p.note && <span className="alb-cell-note">{p.note}</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/* ============================================================
   照片详情：大图 + 谁放的 + 克克的话 + 客观描述 + 移到相册
   ============================================================ */
function PhotoDetail({ photo, albums, onClose, onSaved, setErr }) {
  const [tags, setTags] = useState(photo.tags || []);
  const [addName, setAddName] = useState('');
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify([...tags].sort()) !== JSON.stringify([...(photo.tags || [])].sort());

  // 现有相册名 ∪ 这张已有的标签（防某标签不在清单里时漏显）
  const allNames = [...new Set([...(albums || []), ...(photo.tags || [])])];

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      await apiSetTags(photo.name, tags);
      onSaved();
    } catch (e) { setErr(String(e.message || e)); setSaving(false); }
  }

  function addAlbum() {
    const n = addName.trim();
    if (!n) return;
    setTags((t) => (t.includes(n) ? t : [...t, n]));
    setAddName('');
  }

  return (
    <div className="alb-detail-wrap" onClick={onClose} role="dialog" aria-label="照片详情">
      <div className="alb-detail" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="alb-detail-x" onClick={onClose} aria-label="关闭">
          <X size={18} strokeWidth={2} />
        </button>
        <div className="alb-detail-img">
          <img src={photoUrl(photo.name)} alt="" />
        </div>
        <div className="alb-detail-body">
          <div className="alb-detail-who">
            {photo.by === 'sael' && <Heart size={12} strokeWidth={2.2} />}
            {byLabel(photo.by)}
          </div>
          {photo.note && <p className="alb-detail-note">{photo.note}</p>}
          {photo.caption && !photo.note && <p className="alb-detail-note">{photo.caption}</p>}
          {photo.desc && <p className="alb-detail-desc">画面：{photo.desc}</p>}

          <div className="alb-detail-albsec">
            <div className="alb-detail-label">在这些相册里</div>
            <div className="alb-chips alb-chips-in-detail">
              {allNames.map((n) => (
                <button
                  type="button"
                  key={n}
                  className={'alb-chip' + (tags.includes(n) ? ' on' : '')}
                  onClick={() => setTags((t) => toggleTag(t, n))}
                >
                  {n}
                </button>
              ))}
              <span className="alb-chip alb-chip-new-in">
                <input
                  className="alb-chip-input"
                  value={addName}
                  placeholder="新相册"
                  maxLength={20}
                  onChange={(e) => setAddName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addAlbum(); }}
                />
                <button type="button" className="alb-chip-ok" onClick={addAlbum} aria-label="加">
                  <Plus size={13} strokeWidth={2.4} />
                </button>
              </span>
            </div>
          </div>

          <div className="alb-actions">
            <button
              type="button"
              className="alb-btn alb-btn-go"
              disabled={!dirty || saving}
              onClick={save}
            >
              {saving ? '收好…' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* —— 灯箱组件（FLIP）：大图从被点的缩略图原地长大，关闭缩回原位。
   没有 fromRect / 开了减弱动效 → 直接开直接关（旧行为）。 —— */
function AlbumViewer({ viewer, onClose }) {
  const imgRef = useRef(null);
  const boxRef = useRef(null);
  const closingRef = useRef(false);
  const flip = !!viewer.fromRect && !getFlag('reduce_motion');

  const requestClose = () => {
    if (closingRef.current) return;
    if (!flip) {
      onClose();
      return;
    }
    closingRef.current = true;
    boxRef.current?.classList.add('alb-viewer-closing');
    flipOut(imgRef.current, viewer.fromRect, onClose);
  };

  useEffect(() => {
    if (flip) flipIn(imgRef.current, viewer.fromRect);
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={boxRef}
      className={'alb-viewer' + (flip ? ' alb-viewer-flip' : '')}
      onClick={requestClose}
      role="dialog"
      aria-label="看大图"
    >
      <img ref={imgRef} src={viewer.url} alt="" />
    </div>
  );
}
